import React from 'react';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import styled from 'styled-components';
import { useSignals } from '@preact/signals-react/runtime';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { AiComponentVisualManifest } from '@/pkg/ai';
import { CompoundChange } from '@/pkg/sdk';
import { AiComponentInputCard } from '@/features/editor/widgets/shared/ai-editing-layout/inputs/input-card';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { toGroupName } from '@/features/editor/widgets/shared/ai-editing-layout/utils/group-name';

export function AiComponentVisualEdit() {
  useSignals();
  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const highlightedGroup = useAiComponentContext(
    (ctx) => ctx.highlightedVisualGroup,
  );
  const isProcessingManifest = useAiComponentContext(
    (ctx) => ctx.isProcessingVisualManifest,
  );

  const manifest = useComputed(() => {
    const block = newChange.value.block.value as CompoundChange;
    if (block && block.isAiComponent) {
      return block.editManifest as AiComponentVisualManifest;
    }
    return { components: [] } as AiComponentVisualManifest;
  });

  const hasManifest = useComputed(
    () => manifest.value && manifest.value.components?.length > 0,
  );

  useSignalEffect(() => {
    if (highlightedGroup.value) {
      const highlightElement = (
        selector: string,
        highlightColor = `#f964e7`,
        resetColor = `black`,
        delay = 250,
        duration = 2000,
      ) => {
        const elem = document.querySelector(selector) as HTMLElement;
        if (elem) {
          setTimeout(() => {
            elem.scrollIntoView({
              behavior: `smooth`,
              block: `start`,
              inline: `center`,
            });
            elem.style.color = highlightColor;
            setTimeout(() => {
              elem.style.color = resetColor;
            }, duration);
          }, delay);
          return true;
        }
        return false;
      };

      const groupSelector = `.vsly-group-${toGroupName(
        highlightedGroup.value?.group,
      )}`;
      const selectorSelector = `.vsly-comp-selector-${toGroupName(
        highlightedGroup.value?.selector,
      )}`;

      if (!highlightElement(groupSelector)) {
        highlightElement(selectorSelector);
      }

      highlightedGroup.value = undefined;
    }
  });

  return (
    <Wrapper
      className="visual-edit-tab"
      key={isProcessingManifest.value ? 1 : 0}
    >
      {isProcessingManifest.value && (
        <BackdropSpinner
          marginTop="-5rem"
          loading
          blurAmount={0}
          spinnerSize={40}
          backgroundColor="rgba(0,0,0,0)"
        />
      )}
      {!hasManifest.value && !isProcessingManifest.value && (
        <div>No visual components detected in the HTML.</div>
      )}
      {hasManifest.value &&
        !isProcessingManifest.value &&
        manifest.value.components.map((comp) => (
          <AiComponentInputCard
            key={comp.name + comp.type + comp.selector}
            component={comp}
          />
        ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-y: scroll;
  height: 100%;

  padding-top: 1.5rem;
  padding-bottom: 8rem;

  scroll-padding-top: 1rem;

  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    hsl(0 0% 0% / 1) 3%,
    hsl(0 0% 0% / 1) 95%,
    hsl(0 0% 0% / 0)
  );
`;
